import React, { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw, Inbox, Star, UserPlus, ClipboardList, CalendarOff, Receipt, Mail, LifeBuoy, KeyRound, CloudRain,
  Eye, Users, Timer, CheckCircle2, AlertTriangle, ArrowRight, Server, Sparkles, BellRing, Clock, Building2, CalendarCheck, TrendingUp, TrendingDown, Minus,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { adminGet, reportError } from './adminApi';

const fmtClock = (iso) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : null);

/** Détail du voyant Dolibarr : connecté + dernière réponse, ou indisponible depuis / dernière réponse connue. */
const dolibarrDetail = (d) => {
  if (!d) return 'État inconnu';
  if (d.ok) {
    const last = fmtClock(d.last_success);
    return `Connecté · v${d.version || '?'}${last ? ` · dernière réponse ${last}` : ''}`;
  }
  if (!d.configured) return 'Connexion non configurée';
  const since = fmtClock(d.down_since);
  const last = fmtClock(d.last_success);
  return `Indisponible${since ? ` depuis ${since}` : ''}${last ? ` · dernière réponse ${last}` : ' · aucune réponse depuis le démarrage'}${d.last_error ? ` · ${d.last_error}` : ''}. Les Easers sont en mode dégradé (données mémorisées, envois en attente).`;
};

const fmtHours = (minutes) => {
  const m = Math.max(0, Math.round(minutes || 0));
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
};

const todayLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  const s = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.round(s / 60)} min`;
  return `il y a ${Math.round(s / 3600)} h`;
};

/** Build the "to do" list from the aggregated figures. Only items with a positive count are shown. */
function buildActions(d) {
  const c = d.commercial || {}; const g = d.grh || {}; const cl = d.clients || {}; const t = d.terrain || {};
  const items = [
    { id: 'reviews', count: c.reviews_pending, label: 'avis client(s) à modérer', icon: Star, tab: 'reviews' },
    { id: 'candidatures', count: c.candidatures_new, label: 'nouvelle(s) candidature(s)', icon: UserPlus, tab: 'candidatures' },
    { id: 'requests', count: g.requests_pending, label: 'demande(s) GRH à valider', icon: ClipboardList, tab: 'easers', hint: g.oldest_request_at ? `la plus ancienne ${timeAgo(g.oldest_request_at)}` : null },
    { id: 'leaves', count: g.leaves_pending, label: 'demande(s) de congés en attente', icon: CalendarOff, tab: 'easers' },
    { id: 'expenses', count: g.expenses_pending, label: "note(s) de frais à approuver dans Dolibarr", icon: Receipt, tab: 'easers' },
    { id: 'devis', count: c.devis_nouveau, label: 'demande(s) de devis à traiter', icon: Inbox, tab: 'leads' },
    { id: 'rdv', count: c.rdv_en_attente, label: 'rendez-vous à confirmer', icon: CalendarCheck, tab: 'leads' },
    { id: 'contacts', count: c.contacts_non_lus, label: 'message(s) de contact non lu(s)', icon: Mail, tab: 'leads' },
    { id: 'tickets', count: cl.tickets_open, label: 'ticket(s) client ouvert(s)', icon: LifeBuoy, tab: 'clients' },
    { id: 'codes', count: cl.pending_codes, label: 'code(s) client à communiquer', icon: KeyRound, tab: 'clients', hint: 'SMTP indisponible : à donner par téléphone' },
    { id: 'moral', count: (t.moral?.alerts || []).length, label: 'Easer(s) avec un moral bas aujourd\'hui', icon: CloudRain, tab: 'easers', tone: 'alert' },
    { id: 'not_clocked', count: t.not_clocked_past, label: 'intervention(s) passée(s) non pointée(s)', icon: AlertTriangle, tab: 'easers', tone: 'alert' },
  ];
  return items.filter((i) => typeof i.count === 'number' && i.count > 0);
}

function Stat({ label, value, hint, icon: Icon, tone = 'default', testId }) {
  const toneCls = tone === 'primary'
    ? 'bg-[hsl(var(--primary))] text-primary-foreground'
    : 'bg-card text-foreground border border-border';
  return (
    <div className={`rounded-[var(--radius)] p-4 shadow-sm ${toneCls}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2">
        <p className={`text-xs font-medium ${tone === 'primary' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{label}</p>
        {Icon && <Icon className={`h-4 w-4 ${tone === 'primary' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`} />}
      </div>
      <p className="mt-2 text-2xl font-semibold tracking-tight font-['Space_Grotesk']">{value ?? '—'}</p>
      {hint && <p className={`mt-1 text-xs ${tone === 'primary' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>{hint}</p>}
    </div>
  );
}

function Delta({ today, yesterday }) {
  if (yesterday === undefined || today === undefined) return null;
  const diff = today - yesterday;
  const Icon = diff > 0 ? TrendingUp : diff < 0 ? TrendingDown : Minus;
  return (
    <span className="inline-flex items-center gap-1">
      <Icon className="h-3 w-3" /> {diff > 0 ? '+' : ''}{diff} vs hier ({yesterday})
    </span>
  );
}

function SectionCard({ title, icon: Icon, description, children, error, action, testId }) {
  return (
    <Card className="p-5 shadow-sm" data-testid={testId}>
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-[hsl(var(--brand-mint))] grid place-items-center shrink-0">
            <Icon className="h-4.5 w-4.5 h-[18px] w-[18px] text-[hsl(var(--primary))]" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-tight font-['Space_Grotesk']">{title}</h2>
            {description && <p className="text-xs text-muted-foreground">{description}</p>}
          </div>
        </div>
        {action}
      </div>
      {error ? (
        <p className="text-sm text-destructive flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Données indisponibles : {error}</p>
      ) : children}
    </Card>
  );
}

function HealthLight({ ok, warn, label, detail, icon: Icon, testId }) {
  const dot = ok ? 'bg-[hsl(var(--primary))]' : warn ? 'bg-[hsl(var(--brand-apricot))]' : 'bg-destructive';
  const status = ok ? 'OK' : warn ? 'À surveiller' : 'En panne';
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-3" data-testid={testId}>
      <span className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
          <p className="text-sm font-medium">{label}</p>
          <Badge variant="outline" className="ml-auto text-[11px] shrink-0 whitespace-nowrap">{status}</Badge>
        </div>
        {detail && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{detail}</p>}
      </div>
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div className="space-y-6" data-testid="admin-home-loading">
      <Skeleton className="h-8 w-72" />
      <Skeleton className="h-28 w-full" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Skeleton className="h-64" /><Skeleton className="h-64" /><Skeleton className="h-48" /><Skeleton className="h-48" />
      </div>
    </div>
  );
}

export function AdminHomeTab({ adminKey, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminGet('/api/admin/home', adminKey, refresh ? { refresh: true } : {});
      setData(res.data);
      if (refresh) toast.success('Chiffres actualisés');
    } catch (err) {
      setError(reportError("chargement de l'accueil", err) || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <HomeSkeleton />;

  if (error && !data) {
    return (
      <Card className="p-6 border-destructive/30 bg-destructive/5 flex items-center justify-between gap-3" data-testid="admin-home-error">
        <p className="text-sm text-destructive">{error}</p>
        <Button size="sm" variant="outline" onClick={() => load(true)}>Réessayer</Button>
      </Card>
    );
  }

  const c = data.commercial || {}; const t = data.terrain || {}; const g = data.grh || {}; const cl = data.clients || {}; const h = data.health || {};
  const actions = buildActions(data);
  const hoursPct = t.planned_minutes ? Math.min(100, Math.round((t.actual_minutes / t.planned_minutes) * 100)) : 0;
  const moodLabel = t.moral?.average == null ? 'Pas encore de réponse' : t.moral.average >= 4.5 ? 'Grand soleil' : t.moral.average >= 3.5 ? 'Soleil' : t.moral.average >= 2.5 ? 'Nuage et soleil' : t.moral.average >= 1.5 ? 'Nuageux' : 'Orageux';

  return (
    <div className="space-y-6" data-testid="admin-home-tab">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">{todayLabel(data.today)}</p>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight font-['Space_Grotesk'] mt-1">
            {actions.length === 0 ? 'Tout est à jour, belle journée.' : `${actions.reduce((n, a) => n + a.count, 0)} élément(s) attendent une action`}
          </h2>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3.5 w-3.5" /> Mis à jour {timeAgo(data.generated_at)}
          <Button variant="outline" size="sm" onClick={() => load(true)} disabled={loading} data-testid="admin-home-refresh-button">
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </Button>
        </div>
      </div>

      <SectionCard title="À traiter aujourd'hui" icon={CheckCircle2} description="Les actions en attente, par ordre de priorité" testId="admin-home-actions">
        {actions.length === 0 ? (
          <div className="flex items-center gap-3 rounded-xl bg-[hsl(var(--brand-mint))]/60 p-4" data-testid="admin-home-actions-empty">
            <CheckCircle2 className="h-5 w-5 text-[hsl(var(--primary))]" />
            <p className="text-sm">Aucune action en attente. Les nouveaux avis, candidatures et demandes apparaîtront ici.</p>
          </div>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {actions.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => onNavigate && onNavigate(a.tab)}
                  data-testid={`admin-home-action-${a.id}`}
                  className={`group flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors duration-200 hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${a.tone === 'alert' ? 'border-[hsl(var(--brand-apricot))]/40 bg-[hsl(var(--brand-apricot))]/5' : 'border-border bg-card'}`}
                >
                  <div className={`h-9 w-9 rounded-lg grid place-items-center shrink-0 ${a.tone === 'alert' ? 'bg-[hsl(var(--brand-apricot))]/15' : 'bg-secondary'}`}>
                    <a.icon className={`h-4 w-4 ${a.tone === 'alert' ? 'text-[hsl(var(--brand-apricot))]' : 'text-[hsl(var(--primary))]'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm"><span className="font-semibold">{a.count}</span> {a.label}</p>
                    {a.hint && <p className="text-xs text-muted-foreground truncate">{a.hint}</p>}
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors duration-200" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Activité du site" icon={Eye} description="Visites et demandes reçues aujourd'hui" error={c.error} testId="admin-home-commercial"
          action={<Button variant="ghost" size="sm" onClick={() => onNavigate && onNavigate('analytics')} data-testid="admin-home-goto-analytics">Statistiques <ArrowRight className="h-4 w-4 ml-1" /></Button>}>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Visites aujourd'hui" value={c.visits_today} hint={<Delta today={c.visits_today} yesterday={c.visits_yesterday} />} icon={Eye} tone="primary" testId="admin-home-visits-today" />
            <Stat label="Pages vues" value={c.page_views_today} hint={c.simulator_today ? `${c.simulator_today} simulation(s)` : 'aucune simulation'} icon={Sparkles} />
            <Stat label="Nouveaux leads aujourd'hui" value={c.leads_new_today} hint={`${c.leads_total ?? 0} lead(s) au total`} icon={Inbox} testId="admin-home-leads-today" />
            <Stat label="À traiter" value={(c.devis_nouveau || 0) + (c.rdv_en_attente || 0) + (c.contacts_non_lus || 0)} hint={`${c.devis_nouveau || 0} devis · ${c.rdv_en_attente || 0} RDV · ${c.contacts_non_lus || 0} message(s)`} icon={Mail} />
          </div>
        </SectionCard>

        <SectionCard title="Terrain Easers" icon={Users} description="Interventions et pointages du jour (Dolibarr)" error={t.error} testId="admin-home-terrain"
          action={<Button variant="ghost" size="sm" onClick={() => onNavigate && onNavigate('easers')} data-testid="admin-home-goto-easers">Espace Easer <ArrowRight className="h-4 w-4 ml-1" /></Button>}>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Interventions prévues" value={t.planned_count} hint={`${t.easers_active_today ?? 0} Easer(s) sur ${t.employees_total ?? 0}`} icon={CalendarCheck} tone="primary" testId="admin-home-planned" />
            <Stat label="Terminées / en cours" value={`${t.done_count ?? 0} / ${t.in_progress ?? 0}`} hint={t.not_clocked_past ? `${t.not_clocked_past} passée(s) non pointée(s)` : 'aucun oubli de pointage'} icon={Timer} />
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
              <span>Heures réalisées / prévues</span>
              <span className="font-medium text-foreground" data-testid="admin-home-hours">{fmtHours(t.actual_minutes)} / {fmtHours(t.planned_minutes)}</span>
            </div>
            <Progress value={hoursPct} className="h-2" />
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-secondary/60 p-3" data-testid="admin-home-moral">
            <div className="flex items-center gap-2 min-w-0">
              <CloudRain className="h-4 w-4 text-[hsl(var(--primary))] shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">Moral du jour : {moodLabel}</p>
                <p className="text-xs text-muted-foreground">{t.moral?.answers ?? 0} réponse(s) · {t.moral?.missing ?? 0} sans réponse{t.moral?.average != null ? ` · moyenne ${t.moral.average}/5` : ''}</p>
              </div>
            </div>
            {(t.moral?.alerts || []).length > 0 && <Badge className="bg-[hsl(var(--brand-apricot))]/15 text-[hsl(var(--brand-apricot))] hover:bg-[hsl(var(--brand-apricot))]/15 border border-[hsl(var(--brand-apricot))]/25">{t.moral.alerts.length} alerte(s)</Badge>}
          </div>
        </SectionCard>

        <SectionCard title="Demandes GRH" icon={ClipboardList} description="Ce que les Easers attendent de l'agence" error={g.error} testId="admin-home-grh"
          action={<Button variant="ghost" size="sm" onClick={() => onNavigate && onNavigate('easers')} data-testid="admin-home-goto-grh">Ouvrir <ArrowRight className="h-4 w-4 ml-1" /></Button>}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Demandes" value={g.requests_pending} hint={Object.keys(g.requests_by_type || {}).length ? Object.entries(g.requests_by_type).map(([k, v]) => `${v} ${({ supply: 'fourniture', document: 'document', unavailability: 'indispo', upload: 'envoi' })[k] || k}`).join(' · ') : 'rien en attente'} icon={ClipboardList} testId="admin-home-requests-pending" />
            <Stat label="Congés" value={g.leaves_pending} hint="à valider" icon={CalendarOff} />
            <Stat label="Notes de frais" value={g.expenses_pending ?? '—'} hint="à approuver" icon={Receipt} />
          </div>
        </SectionCard>

        <SectionCard title="Portail client" icon={Building2} description="Comptes, tickets et accès en attente" error={cl.error} testId="admin-home-clients"
          action={<Button variant="ghost" size="sm" onClick={() => onNavigate && onNavigate('clients')} data-testid="admin-home-goto-clients">Clients <ArrowRight className="h-4 w-4 ml-1" /></Button>}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Stat label="Comptes actifs" value={cl.accounts} hint={`${cl.ratings_total ?? 0} évaluation(s)`} icon={Users} />
            <Stat label="Tickets ouverts" value={cl.tickets_open} hint="à répondre" icon={LifeBuoy} />
            <Stat label="Codes en attente" value={cl.pending_codes} hint="à communiquer" icon={KeyRound} />
          </div>
        </SectionCard>
      </div>

      <SectionCard title="Santé technique" icon={Server} description="Voyants des services dont dépend l'application" error={h.error} testId="admin-home-health"
        action={<Button variant="ghost" size="sm" onClick={() => onNavigate && onNavigate('news')} data-testid="admin-home-goto-settings">Paramètres <ArrowRight className="h-4 w-4 ml-1" /></Button>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <HealthLight ok={h.dolibarr?.ok} label="Dolibarr" icon={Server} testId="admin-home-health-dolibarr" detail={dolibarrDetail(h.dolibarr)} />
          <HealthLight ok={h.smtp?.ok} warn={!h.smtp?.configured} label="E-mails (SMTP)" icon={Mail} testId="admin-home-health-smtp"
            detail={h.smtp?.ok ? 'Envois OK' : !h.smtp?.configured ? 'Non configuré' : `${h.smtp?.errors_24h || 0} échec(s) en 24 h · ${h.smtp?.last_error || ''}`} />
          <HealthLight ok={h.ai_keys?.ok} label="Clés IA" icon={Sparkles} testId="admin-home-health-ai"
            detail={h.ai_keys?.ok ? (h.ai_keys.configured || []).join(', ') : 'Aucune clé configurée'} />
          <HealthLight ok={h.push?.ok} warn={!h.push?.ok} label="Notifications push" icon={BellRing} testId="admin-home-health-push"
            detail={h.push?.ok ? `${h.push.users} Easer(s) abonné(s) · ${h.push.subscriptions} appareil(s)` : 'Aucun Easer abonné pour le moment'} />
          <HealthLight ok={h.weather?.ok} warn={h.weather?.degraded || (!h.weather?.last_success && !h.weather?.last_error)} label="Météo Easer" icon={CloudRain} testId="admin-home-health-weather"
            detail={h.weather?.ok ? `Prévisions via ${h.weather.provider}` : h.weather?.degraded ? `Repli sur ${h.weather.provider} · ${h.weather.last_error || ''}` : h.weather?.last_error ? h.weather.last_error : 'Pas encore sollicitée depuis le démarrage'} />
          <HealthLight ok={h.scheduler?.ok} label="Tâches planifiées" icon={Clock} testId="admin-home-health-scheduler"
            detail={h.scheduler?.ok ? `${h.scheduler.jobs} tâche(s) actives` : 'Planificateur arrêté'} />
        </div>
      </SectionCard>
    </div>
  );
}
